import { FormControlLabel, FormHelperText, IconButton, InputAdornment, Stack, SxProps } from '@mui/material';
import { Label } from '../../Label';
import { ChangeEvent, FC } from 'react';
import { Controller, get } from 'react-hook-form';
import { Input } from '../../Input';
import { ReactComponent as CrossIcon } from './cross.svg';
import { ErrorHelperText } from '@components/ErrorHelperText';
import { buildDataTestIdAttribute, buildDataTestIdFromNestedName } from '@src/utils';

type FormTextFieldProps = {
  name: string;
  label: string;
  required?: boolean;
  control?: any;
  placeholder?: string;
  clearable?: boolean;
  sx?: SxProps;
  regex?: RegExp;
  maxLength?: number;
  withErrorMessage?: boolean;
  disabled?: boolean;
  withMaxLenghtHelper?: boolean;
  withReplaceComma?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClear?: () => void;
  errorManual?: string;
  dataTestId?: string;
};

export const FormTextField: FC<FormTextFieldProps> = ({
  name,
  label,
  required,
  control,
  placeholder = '',
  sx,
  regex,
  clearable = false,
  maxLength,
  withErrorMessage = false,
  withMaxLenghtHelper = false,
  withReplaceComma = false,
  disabled,
  onChange,
  onClear,
  errorManual,
  dataTestId,
  ...props
}) => {
  const inputDataTestId = dataTestId || buildDataTestIdFromNestedName(name);
  return (
    <FormControlLabel
      labelPlacement="top"
      name={name}
      label={<Label label={label} required={required} disabled={disabled} />}
      sx={{ width: '100%', ...sx }}
      control={
        <Controller
          name={name}
          control={control}
          render={({ field: { ref, value, ...field }, formState }) => {
            const errorField = get(formState.errors, name);
            const error = (errorField && (errorField.message as string)) || errorManual;

            return (
              <>
                {maxLength && withMaxLenghtHelper && (
                  <Stack direction="row" justifyContent="flex-end" width="100%" mt="3px">
                    <FormHelperText
                      sx={{
                        order: '-1',
                        height: 0,
                      }}
                    >
                      {value?.length || 0}/{maxLength}
                    </FormHelperText>
                  </Stack>
                )}
                <>
                  <Input
                    {...field}
                    value={value || ''}
                    placeholder={placeholder}
                    color="secondary"
                    variant="outlined"
                    disabled={disabled}
                    error={!!error}
                    onChange={e => {
                      let { value: fieldValue } = e.target;
                      // eslint-disable-next-line unicorn/prefer-string-replace-all
                      if (withReplaceComma) fieldValue = fieldValue.replaceAll(/,/g, '.');
                      if (regex) {
                        if (regex.test(fieldValue) || !fieldValue) {
                          field.onChange(fieldValue);
                          onChange && onChange(e);
                        } else return;
                      } else {
                        field.onChange(fieldValue);
                        onChange && onChange(e);
                      }
                    }}
                    fullWidth
                    sx={{
                      minWidth: '64px',
                      '& .MuiInputBase-root': { paddingLeft: '12px' },
                      '& .MuiOutlinedInput-input': {
                        padding: '11px 0px',
                      },
                    }}
                    InputProps={{
                      endAdornment: clearable && value && !disabled && (
                        <InputAdornment position="end" sx={{ marginLeft: 0 }}>
                          <IconButton
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              field.onChange('');
                              onClear && onClear();
                            }}
                            size="small"
                            {...buildDataTestIdAttribute(inputDataTestId, 'button-cross')}
                          >
                            <CrossIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      inputProps: { maxLength, ...buildDataTestIdAttribute(inputDataTestId) },
                    }}
                    {...props}
                  />
                  {withErrorMessage && <ErrorHelperText error={error} />}
                </>
              </>
            );
          }}
        />
      }
    />
  );
};
